/**
 * useUserSettings Hook
 * Orchestrates mediator calls and store updates for user preferences
 */

import { useCallback, useEffect } from 'react';
import { mediator } from '@/application/core';
import {
  GetUserPreferencesQuery,
  type UserPreferences,
} from '@/application/queries/userPreferences';
import { UpdateUserPreferencesCommand } from '@/application/commands/userPreferences';
import { navigatePortalInternal } from '@/components/common/PortalLayout/PortalLayout.store';
import { useUserSettingsStore } from './UserSettingsPage.store';

export function useUserSettings() {
  // Individual selectors (per zustand-patterns.md)
  const data = useUserSettingsStore((s) => s.data);
  const loading = useUserSettingsStore((s) => s.loading);
  const error = useUserSettingsStore((s) => s.error);
  const saving = useUserSettingsStore((s) => s.saving);
  const saveError = useUserSettingsStore((s) => s.saveError);
  const saveSuccess = useUserSettingsStore((s) => s.saveSuccess);
  const setData = useUserSettingsStore((s) => s.setData);
  const setLoading = useUserSettingsStore((s) => s.setLoading);
  const setError = useUserSettingsStore((s) => s.setError);
  const setSaving = useUserSettingsStore((s) => s.setSaving);
  const setSaveError = useUserSettingsStore((s) => s.setSaveError);
  const setSaveSuccess = useUserSettingsStore((s) => s.setSaveSuccess);

  // Fetch preferences on mount
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    mediator
      .send<UserPreferences>(new GetUserPreferencesQuery())
      .then((prefs) => {
        if (!cancelled) setData(prefs);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err : new Error(String(err)));
      });

    return () => {
      cancelled = true;
    };
  }, [setData, setError, setLoading]);

  // Save preferences — if language changed, redirect via /switch-language for cookie update
  const savePreferences = useCallback(
    async (values: UserPreferences) => {
      setSaving(true);
      setSaveError(null);
      setSaveSuccess(false);

      try {
        await mediator.send<UserPreferences>(new UpdateUserPreferencesCommand(values));
        setSaveSuccess(true);

        // Language change requires full page reload to apply new translations
        if (data?.language !== values.language && values.language) {
          const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
          navigatePortalInternal(`/switch-language?culture=${values.language}&returnUrl=${returnUrl}`);
        }
      } catch (err) {
        setSaveError(err instanceof Error ? err : new Error(String(err)));
      }
    },
    [data?.language, setSaving, setSaveError, setSaveSuccess]
  );

  return { data, loading, error, saving, saveError, saveSuccess, savePreferences };
}
